import React from 'react';
import { ComponentWithAs, IconProps } from '@chakra-ui/react';

import { NotificationSeverity, NotificationType } from 'clipsal-cortex-types/src/api/api-notifications';

import { VPPIcon } from '../../styles/custom-icons';
import {
  BatteryErrorIcon,
  BatteryLowNotificationIcon,
  BCSErrorIcon,
  DeviceOfflineIcon,
  GridOutageIcon,
  GridRestoredIcon,
  InverterErrorIcon,
  InverterIcon,
  LowPowerModeActiveIcon,
  MeterErrorIcon,
  NewDeviceDetectedIcon,
  OffGridModeActiveIcon,
  StormModeActiveIcon,
  TestNotificationIcon,
} from '../../styles/notification-icons';
import { CallSupportButton } from './CallSupportButton';
import { ConfigureDeviceButton } from './ConfigureDeviceButton';
import { NotificationSwitchesList } from './NotificationSwitchesList';
import { ViewVPPButton } from './ViewVPPButton';

export const NOTIFICATION_POLLING_TIME_MS = 15000;

export const NOTIFICATION_TYPE_TO_CONFIG: Record<
  NotificationType,
  {
    icon: ComponentWithAs<'svg', IconProps>;
    width?: number; // default is 10
    height?: number; // default is 10
    testId?: string;
    child?: React.JSX.Element;
  }
> = {
  LOW_BATTERY: {
    icon: BatteryLowNotificationIcon,
    testId: 'battery-low-notification-icon',
    child: <NotificationSwitchesList />,
  },
  TEST: { icon: TestNotificationIcon },
  // @NOTE: This hasn't been finalized yet, just using the test one
  START_CHARGING: {
    icon: TestNotificationIcon,
    testId: 'charge-started-notification-icon',
  },
  GRID_RESTORED: {
    icon: GridRestoredIcon,
    testId: 'grid-restored-notification-icon',
    child: <NotificationSwitchesList type="GRID_RESTORED" />,
  },
  GRID_OUTAGE: {
    icon: GridOutageIcon,
    testId: 'grid-outage-notification-icon',
    child: <NotificationSwitchesList />,
  },
  NEW_DEVICE_DETECTED: {
    icon: NewDeviceDetectedIcon,
    testId: 'new-device-detected-notification-icon',
    child: <ConfigureDeviceButton />,
  },
  STORM_MODE_TRIGGERED: {
    icon: StormModeActiveIcon,
    testId: 'storm-mode-active-notification-icon',
  },
  STORM_MODE_DEACTIVATE_SOON: {
    icon: StormModeActiveIcon,
    testId: 'storm-mode-deactivate-soon-notification-icon',
  },
  OFF_GRID_MODE_TRIGGERED: {
    icon: OffGridModeActiveIcon,
    testId: 'off-grid-mode-active-notification-icon',
  },
  LOW_POWER_MODE_TRIGGERED: {
    icon: LowPowerModeActiveIcon,
    testId: 'low-power-mode-active-notification-icon',
  },
  HARDWARE_CONNECTION_LOST_SENSE: {
    icon: DeviceOfflineIcon,
    testId: 'meter-connection-lost-notification-icon',
  },
  HARDWARE_CONNECTION_LOST_SATURN: {
    icon: DeviceOfflineIcon,
    testId: 'inverter-connection-lost-notification-icon',
  },
  INVERTER_ERROR_DETECTED: {
    icon: InverterErrorIcon,
    testId: 'inverter-error-notification-icon',
    child: <CallSupportButton />,
  },
  INVERTER_ONLINE: {
    icon: InverterIcon,
    testId: 'inverter-online-notification-icon',
  },
  INVERTER_OFFLINE: {
    icon: InverterErrorIcon,
    testId: 'inverter-error-notification-icon',
  },
  OVERLOAD_NOTICE: {
    icon: InverterErrorIcon,
    testId: 'inverter-error-notification-icon',
    child: <NotificationSwitchesList type="OVERLOAD_NOTICE" />,
  },
  OVERLOAD_TRIPPED: {
    icon: InverterErrorIcon,
    testId: 'inverter-error-notification-icon',
    child: <NotificationSwitchesList type="OVERLOAD_TRIPPED" />,
  },
  BATTERY_ERROR_DETECTED: {
    icon: BatteryErrorIcon,
    testId: 'battery-error-notification-icon',
    child: <CallSupportButton />,
  },
  METER_ERROR_DETECTED: {
    icon: MeterErrorIcon,
    testId: 'meter-error-notification-icon',
    child: <CallSupportButton />,
  },
  BCS_ERROR_DETECTED: {
    icon: BCSErrorIcon,
    testId: 'bcs-error-notification-icon',
    child: <CallSupportButton />,
  },
  VPP_PROGRAM_ENROLLED: { icon: VPPIcon, testId: 'vpp-program-enrolled-notification-icon', child: <ViewVPPButton /> },
  VPP_PROGRAM_PENDING: { icon: VPPIcon, testId: 'vpp-program-pending-notification-icon', child: <ViewVPPButton /> },
  VPP_PROGRAM_ELIGIBLE: { icon: VPPIcon, testId: 'vpp-program-eligible-notification-icon', child: <ViewVPPButton /> },
  VPP_PROGRAM_INELIGIBLE: {
    icon: VPPIcon,
    testId: 'vpp-program-ineligible-notification-icon',
    child: <ViewVPPButton />,
  },
  VPP_PROGRAM_UNSUBSCRIBED: {
    icon: VPPIcon,
    testId: 'vpp-program-unsubscribed-notification-icon',
    child: <ViewVPPButton />,
  },
  VPP_EVENT_SCHEDULED: { icon: VPPIcon, testId: 'vpp-event-scheduled-notification-icon', child: <ViewVPPButton /> },
};

/**
 * Map of notification severity to icon color.
 * This is used to set the color of the icon in the notification list when sorted by priority.
 */
export const NOTIFICATION_SEVERITY_TYPE_TO_COLOR_MAP: Record<NotificationSeverity, string> = {
  INFO: '#3DCD57',
  WARNING: '#FFA924',
  ALARM: '#EC510E',
};
